import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Bed,
  Calendar,
  Heart,
  Home,
  MapPin,
  Menu,
  Search,
  ShieldCheck,
  Shuffle,
  Star,
  X,
} from 'lucide-react';

import { useToast } from '../../contexts/ToastContext';
import { useBrand } from '../../contexts/BrandContext';
import { apiService } from '../../services/api';
import { extractError, getHostelCoverImage } from '@/utils';
import PlaceholderImage from '../../components/ui/PlaceholderImage';
import { BookingClosedBanner } from '../../components/notices/BookingClosedNotice';
import type { Hostel } from '../../types';


const painPoints = (brandName: string) => [
  {
    icon: ShieldCheck,
    iconBg: 'bg-destructive-subtle',
    iconColor: 'text-destructive',
    problem: `Verified ${brandName} hostels`,
    studentBenefit: `Browse only official ${brandName} accommodation options, with current room and bed availability from the management system.`,
  },
  {
    icon: Shuffle,
    iconBg: 'bg-warning-subtle',
    iconColor: 'text-warning',
    problem: 'Clear booking or balloting',
    studentBenefit: 'Book open beds directly or join ballot-based allocations where demand is high. Your account keeps the process traceable.',
  },
  {
    icon: Bed,
    iconBg: 'bg-info-subtle',
    iconColor: 'text-info',
    problem: 'One payment can cover multiple students',
    studentBenefit: 'Parents and guardians can select multiple bed spaces, enter each student’s name and matric number, and upload one transfer receipt.',
  },
];

const howItWorks = (brandName: string) => [
  { step: '01', title: 'Create your account', desc: 'Register or sign in before booking starts so every reservation is tied to a student profile.' },
  { step: '02', title: 'Find hostel', desc: `Browse ${brandName} hostels, compare prices, availability, amenities, and allocation mode.` },
  { step: '03', title: 'Select bed spaces', desc: 'Pick one bed or multiple bed spaces for different students, then add each student’s name and matric number.' },
  { step: '04', title: 'Submit payment', desc: 'Pay by card for eligible single bookings or upload one transfer receipt for grouped bookings.' },
];

const testimonials = (brandName: string) => [
  {
    name: 'Student booking',
    role: 'Direct reservation',
    text: `Create an account, choose an available ${brandName} bed space, and complete payment from the student portal.`,
    rating: 5,
  },
  {
    name: 'Parent payment',
    role: 'Grouped transfer',
    text: 'Select multiple bed spaces, enter each student’s details, and submit one receipt for review.',
    rating: 5,
  },
  {
    name: 'Ballot entry',
    role: 'High-demand hostel',
    text: 'Join a hostel ballot from your account and return to track allocation status.',
    rating: 4,
  },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useToast();
  const { tenant, platformName, bookingWindow } = useBrand();
  const bookingClosed = bookingWindow.status === 'CLOSED';
  // Either switch sends the CTA to support instead of signup: applications can
  // be shut while signup stays open, and vice versa.
  const signupUnavailable = bookingClosed || !bookingWindow.registrationOpen;
  const [searchQuery, setSearchQuery] = useState('');
  const [semester, setSemester] = useState('This Semester');
  const [loadingHostels, setLoadingHostels] = useState(false);
  const [hostels, setHostels] = useState<Hostel[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    let active = true;

    const loadHostels = async () => {
      try {
        setLoadingHostels(true);
        const data = await apiService.getHostels();
        if (!active) {
          return;
        }
        const safe = Array.isArray(data) ? data : [];
        setHostels(safe.filter((hostel) => hostel.isActive));
      } catch (error: any) {
        if (active) {
          showError('Unable to load hostels', extractError(error, 'Please try again.'));
        }
      } finally {
        if (active) {
          setLoadingHostels(false);
        }
      }
    };

    loadHostels();

    return () => {
      active = false;
    };
  }, [showError]);

  const featuredHostels = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const filtered = hostels.filter((hostel) => {
      if (!query) {
        return true;
      }
      return (
        hostel.name.toLowerCase().includes(query) ||
        (hostel.address || '').toLowerCase().includes(query)
      );
    });

    return filtered.slice(0, 6);
  }, [hostels, searchQuery]);

  const platformStats = useMemo(
    () => [
      { value: String(hostels.length || '—'), label: 'Hostel Options', desc: 'Across campus zones' },
      { value: '24h', label: 'Transfer Review', desc: 'Receipt confirmation window' },
      { value: '1', label: 'Student Account', desc: 'Required before booking' },
    ],
    [hostels.length],
  );

  return (
    <div className="min-h-screen bg-background">
      <BookingClosedBanner />
      {/* ── Header ── */}
      <header className="sticky top-0 z-50 border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-4 py-3 sm:py-4 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Home className="h-4 w-4 text-primary-foreground" />
              </div>
              <span className="text-lg font-bold text-foreground sm:text-xl" style={{ fontFamily: 'var(--font-display)' }}>
                {platformName}
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden items-center gap-8 md:flex">
              <Link to="/" className="text-sm font-medium text-foreground transition-colors hover:text-primary">
                Home
              </Link>
              <Link to="/booking/reserve" className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
                Hostels
              </Link>
              <a href="#about" className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
                About
              </a>
            </nav>

            {/* Desktop Actions */}
            <div className="hidden items-center gap-3 md:flex">
              {bookingWindow.registrationOpen && (
                <Link to="/register">
                  <button className="rounded-full border border-border px-5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted">
                    Create Account
                  </button>
                </Link>
              )}
              {bookingWindow.signInOpen && (
                <Link to="/login">
                  <button className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary">
                    Student Sign In
                  </button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted md:hidden"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="mt-3 border-t border-border pt-3 md:hidden">
              <nav className="flex flex-col gap-1">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                >
                  Home
                </Link>
                <Link
                  to="/booking/reserve"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  Hostels
                </Link>
                <a
                  href="#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
                >
                  About
                </a>
                {bookingWindow.registrationOpen && (
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    className="mt-2 rounded-full border border-border px-5 py-2 text-center text-sm font-medium text-foreground transition-colors hover:bg-muted"
                  >
                    Create Account
                  </Link>
                )}
                {bookingWindow.signInOpen && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="rounded-full bg-primary px-5 py-2 text-center text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary"
                  >
                    Student Sign In
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="bg-background pb-10 pt-8 sm:pb-16 sm:pt-12">
        <div className="mx-auto max-w-7xl px-4 text-center lg:px-8">
          {/* Badge */}
          <div className="animate-rise-in inline-flex items-center gap-2 rounded-full bg-brand-subtle px-3 py-1.5 text-xs font-medium text-primary sm:px-4 sm:py-2 sm:text-sm">
            <BadgeCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Official {platformName} booking portal
          </div>

          {/* Headline - Student-first */}
          <h1 className="animate-rise-in delay-1 mt-5 text-2xl font-bold text-foreground sm:mt-6 sm:text-4xl lg:text-5xl" style={{ fontFamily: 'var(--font-display)' }}>
            Your Student Accommodation
          </h1>
          <h1 className="animate-rise-in delay-1 mt-1 text-2xl font-bold sm:text-4xl lg:text-5xl" style={{ fontFamily: 'var(--font-display)', color: '#0F766E' }}>
            Starts Here.
          </h1>

          {/* Subtitle */}
          <p className="animate-rise-in delay-2 mx-auto mt-4 max-w-3xl px-2 text-sm text-muted-foreground sm:mt-5 sm:px-0 sm:text-base lg:text-lg">
            {tenant.heroCopy.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="animate-rise-in delay-3 mt-6 flex flex-col items-center justify-center gap-3 sm:mt-8 sm:flex-row">
            <Link to="/booking/reserve" className="w-full sm:w-auto">
              <button className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-teal-600/25 transition-all hover:bg-primary hover:shadow-xl sm:w-auto">
                {bookingClosed ? 'View Hostels' : 'Find Hostel'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to={signupUnavailable ? '/support' : '/register'} className="w-full sm:w-auto">
              <button className="w-full rounded-full border border-border bg-background px-8 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted sm:w-auto">
                {signupUnavailable ? 'Contact Support' : 'Create Account to Book'}
              </button>
            </Link>
          </div>

          {/* Social Proof Stats */}
          <div className="animate-rise-in delay-4 mx-auto mt-10 grid max-w-3xl grid-cols-3 gap-2 rounded-2xl border border-border bg-muted p-4 sm:mt-12 sm:gap-8 sm:p-6">
            {platformStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-xl font-bold text-primary sm:text-3xl">{stat.value}</p>
                <p className="mt-0.5 text-[10px] font-medium text-foreground sm:text-sm">{stat.label}</p>
                <p className="hidden text-xs text-muted-foreground sm:block">{stat.desc}</p>
              </div>
            ))}
          </div>

          {/* Search Bar - Desktop */}
          <div className="animate-rise-in delay-4 mx-auto mt-8 hidden max-w-2xl sm:mt-10 sm:block">
            <div className="flex items-center gap-2 rounded-full border border-border bg-background p-2 shadow-sm">
              <div className="flex flex-1 items-center gap-2 pl-4">
                <MapPin className="h-5 w-5 text-muted-foreground" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  placeholder={`Search ${platformName}...`}
                />
              </div>
              <div className="flex items-center gap-2 border-l border-border px-4">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <select
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                  className="border-0 bg-transparent text-sm text-muted-foreground outline-none"
                >
                  <option>This Semester</option>
                  <option>Next Semester</option>
                  <option>Full Year</option>
                </select>
              </div>
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (searchQuery.trim()) {
                    params.set('search', searchQuery.trim());
                  }
                  if (semester && semester !== 'This Semester') {
                    params.set('semester', semester);
                  }
                  navigate(`/booking/reserve${params.toString() ? `?${params.toString()}` : ''}`);
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Search Bar - Mobile */}
          <div className="animate-rise-in delay-4 mx-auto mt-6 sm:hidden">
            <div className="space-y-3 rounded-2xl border border-border bg-background p-4 shadow-sm">
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2.5">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  placeholder={`Search ${platformName}...`}
                />
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <select
                  value={semester}
                  onChange={(e) => setSemester(e.target.value)}
                  className="w-full border-0 bg-transparent text-sm text-muted-foreground outline-none"
                >
                  <option>This Semester</option>
                  <option>Next Semester</option>
                  <option>Full Year</option>
                </select>
              </div>
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (searchQuery.trim()) {
                    params.set('search', searchQuery.trim());
                  }
                  if (semester && semester !== 'This Semester') {
                    params.set('semester', semester);
                  }
                  navigate(`/booking/reserve${params.toString() ? `?${params.toString()}` : ''}`);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary"
              >
                <Search className="h-4 w-4" />
                Find Hostel
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Spaces ── */}
      <section className="bg-background py-10 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground sm:text-2xl lg:text-3xl" style={{ fontFamily: 'var(--font-display)' }}>
                {platformName}
              </h2>
              <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                Official hostel options with live availability and booking mode.
              </p>
            </div>
            <Link
              to="/booking/reserve"
              className="mt-2 flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary sm:mt-0"
            >
              View all hostels
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 grid gap-4 sm:mt-8 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {loadingHostels
              ? [0, 1, 2].map((index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-2xl border border-border bg-background p-4 shadow-sm"
                  >
                    <div className="h-44 animate-pulse rounded-xl bg-muted" />
                    <div className="mt-4 h-4 w-2/3 animate-pulse rounded bg-muted" />
                    <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-muted" />
                    <div className="mt-5 h-9 w-28 animate-pulse rounded-full bg-muted" />
                  </div>
                ))
              : null}

            {!loadingHostels
              ? featuredHostels.map((hostel, index) => {
                  const tags =
                    hostel.amenities?.slice(0, 3) && hostel.amenities.length > 0
                      ? hostel.amenities.slice(0, 3)
                      : [hostel.gender, `${hostel.totalRooms} Rooms`, 'Verified'];
                  const badge = hostel.ballotingEnabled
                    ? { text: 'BALLOT MODE', color: 'bg-accent' }
                    : { text: 'OPEN BOOKING', color: 'bg-primary' };
                  const listingPath = `/booking/reserve?hostelId=${encodeURIComponent(hostel.id)}`;
                  const hasBedMetrics =
                    typeof hostel.availableBedSpaces === 'number' &&
                    typeof hostel.totalBedSpaces === 'number' &&
                    hostel.totalBedSpaces > 0;
                  const availabilityLabel = hasBedMetrics
                    ? `${hostel.availableBedSpaces}/${hostel.totalBedSpaces} bed spaces available`
                    : `${hostel.availableRooms}/${hostel.totalRooms} rooms available`;
                  const coverImage = getHostelCoverImage(hostel.images);

                  return (
                    <div
                      key={hostel.id}
                      className={`animate-rise-in delay-${index + 1} group overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={hostel.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <PlaceholderImage
                            name={hostel.name}
                            className="h-full w-full transition-transform duration-300 group-hover:scale-105"
                          />
                        )}
                        <div className="absolute left-3 top-3">
                          <span
                            className={`inline-flex items-center rounded-md ${badge.color} px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white`}
                          >
                            {badge.text}
                          </span>
                        </div>
                        <button className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-sm transition-colors hover:bg-white">
                          <Heart className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>

                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <h3
                            className="text-base font-bold text-foreground"
                            style={{ fontFamily: 'var(--font-display)' }}
                          >
                            {hostel.name}
                          </h3>
                          <div className="flex items-center gap-1 text-xs font-semibold text-primary">
                            <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                            LIVE
                          </div>
                        </div>

                        <div className="mt-1 flex items-center gap-1 text-sm text-muted-foreground min-w-0">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{hostel.address || 'University Campus'}</span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end sm:justify-between sm:gap-0">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {availabilityLabel}
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              See pricing on the details page
                            </p>
                          </div>
                          <Link to={listingPath} className="w-full sm:w-auto">
                            <button className="w-full rounded-full border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-brand-subtle sm:w-auto">
                              View Details
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })
              : null}
          </div>

          {!loadingHostels && featuredHostels.length === 0 ? (
            <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted p-8 text-center">
              <p className="text-sm text-muted-foreground">No active hostels found yet.</p>
              <Link
                to="/booking/reserve"
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:text-primary"
              >
                Open booking page
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}
        </div>
      </section>

      {/* ── Booking Rules ── */}
      <section id="about" className="bg-muted py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {/* Section Header */}
          <div className="mb-10 text-center sm:mb-14">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary sm:text-sm">Before You Book</p>
            <h2 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl" style={{ fontFamily: 'var(--font-display)' }}>
              Booking Starts After Account Creation
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Every booking, ballot entry, receipt upload, and payment review is connected to a signed-in student account.
            </p>
          </div>

          {/* Pain Points Grid */}
          <div className="grid gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3">
            {painPoints(platformName).map(({ icon: Icon, iconBg, iconColor, problem, studentBenefit }) => (
              <div key={problem} className="rounded-2xl border border-border bg-background p-5 shadow-sm sm:p-6">
                <div className={`mb-4 inline-flex items-center justify-center rounded-xl p-3 ${iconBg}`}>
                  <Icon className={`h-6 w-6 ${iconColor}`} />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wider text-destructive">{platformName}</p>
                <h3 className="mt-1 text-base font-bold text-foreground sm:text-lg">{problem}</h3>
                <div className="my-3 border-t border-border" />
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">What this means</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">{studentBenefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Booking Paths ── */}
      <section className="bg-background py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="mb-10 text-center sm:mb-14">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary sm:text-sm">Booking Paths</p>
            <h2 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl" style={{ fontFamily: 'var(--font-display)' }}>
              Choose the Route That Fits Your Hostel
            </h2>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3">
            {testimonials(platformName).map(({ name, role, text, rating }) => (
              <div key={name} className="rounded-2xl border border-border bg-background p-5 shadow-sm sm:p-6">
                <div className="mb-3 flex gap-0.5">
                  {Array.from({ length: rating }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">"{text}"</p>
                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-sm font-semibold text-foreground">{name}</p>
                  <p className="text-xs text-muted-foreground">{role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="bg-muted py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          {/* Section Header */}
          <div className="mb-10 text-center sm:mb-14">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary sm:text-sm">Simple Process</p>
            <h2 className="mt-2 text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl" style={{ fontFamily: 'var(--font-display)' }}>
              From Account to Booking Review
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
              Students and parents can complete the flow without anonymous checkout or manual follow-up.
            </p>
          </div>

          {/* Steps */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {howItWorks(platformName).map(({ step, title, desc }) => (
              <div key={step} className="relative">
                <div className="mb-3 text-4xl font-bold text-teal-100 sm:text-5xl">{step}</div>
                <h3 className="text-base font-bold text-foreground sm:text-lg">{title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">{desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-10 text-center sm:mt-14">
            <Link to="/booking/reserve">
              <button className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-teal-600/25 transition-all hover:bg-primary hover:shadow-xl">
                {bookingClosed ? 'View Hostels' : 'Find Hostel'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </Link>
            <Link to={signupUnavailable ? '/support' : '/register'} className="ml-0 mt-3 inline-flex sm:ml-3 sm:mt-0">
              <button className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-8 py-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted">
                {signupUnavailable ? 'Contact Support' : 'Create Account to Book'}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border bg-background">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:py-12 lg:px-8">
          <div className="grid grid-cols-2 gap-6 sm:gap-8 md:grid-cols-4">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <Home className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold text-foreground" style={{ fontFamily: 'var(--font-display)' }}>
                  {platformName}
                </span>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                {tenant.footerCopy.blurb}
              </p>
            </div>

            {/* For Students */}
            <div>
              <h4 className="text-xs font-semibold text-foreground sm:text-sm">For Students</h4>
              <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-3">
                {['Find Hostel', 'Create Account', 'Track Booking', 'Help Desk']
                  .filter(
                    (label) =>
                      (label !== 'Create Account' || bookingWindow.registrationOpen) &&
                      (label !== 'Track Booking' || bookingWindow.signInOpen) &&
                      // Support is the only route left when everything else is
                      // shut, so it takes the vacated slot rather than leaving
                      // students a footer with one dead-ended link.
                      (label !== 'Help Desk' || !bookingWindow.signInOpen),
                  )
                  .map((label) => (
                  <li key={label}>
                    <Link
                      to={
                        label === 'Create Account'
                          ? '/register'
                          : label === 'Track Booking'
                            ? '/login'
                            : label === 'Help Desk'
                              ? '/support'
                              : '/booking/reserve'
                      }
                      className="text-xs text-muted-foreground transition-colors hover:text-primary sm:text-sm"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hostels */}
            <div>
              <h4 className="text-xs font-semibold text-foreground sm:text-sm">Hostels</h4>
              <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-3">
                {hostels.length > 0 ? (
                  hostels.slice(0, 3).map((hostel) => (
                    <li key={hostel.id}>
                      <Link
                        to={`/booking/reserve?hostelId=${encodeURIComponent(hostel.id)}`}
                        className="text-xs text-muted-foreground transition-colors hover:text-primary sm:text-sm"
                      >
                        {hostel.name}
                      </Link>
                    </li>
                  ))
                ) : (
                  <li>
                    <Link to="/booking/reserve" className="text-xs text-muted-foreground transition-colors hover:text-primary sm:text-sm">
                      View all hostels
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-xs font-semibold text-foreground sm:text-sm">Support</h4>
              <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-3">
                {['Payment Review', 'Balloting', 'Help Desk'].map((label) => (
                  <li key={label}>
                    <Link to={label === 'Help Desk' ? '/support' : '/booking/reserve'} className="text-xs text-muted-foreground transition-colors hover:text-primary sm:text-sm">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-border pt-6 sm:mt-10 sm:flex-row sm:gap-0">
            <p className="text-[10px] text-muted-foreground sm:text-xs">© 2026 {platformName}. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-muted-foreground transition-colors hover:text-foreground" aria-label="Twitter">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a href="#" className="text-muted-foreground transition-colors hover:text-foreground" aria-label="Instagram">
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
